import { Component, OnInit, OnDestroy, ViewChildren, HostListener, inject, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { debounceTime, filter, lastValueFrom, skip, Subject, Subscription, takeUntil } from 'rxjs';
import { Entities } from '../../models/globals';
import { QuickAccessControl } from '../../models/quickAccessControl';
import { UserRoleNames } from '../../models/users';
import { AuthenticationService } from '../../services/authentication.service';
import { DeploymentService } from '../../services/deployment.service';
import { HeaderService } from '../../services/header.service';
import { NavigationService } from '../../services/navigation.service';
import { StorageDataTransferService } from '../../services/storage-data-transfer.service';
import { StorageService } from '../../services/storage.service';
import { UserService } from '../../services/user.service';
import { EnvironmentService } from '../../services/environment.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
})
export class MenuComponent implements OnInit, OnDestroy {
  @ViewChildren('myDrop') myDrop: any;

  // Injected services
  _userService = inject(UserService);
  _authService = inject(AuthenticationService);
  _headerService = inject(HeaderService);
  _depService = inject(DeploymentService);
  _router = inject(Router);
  _route = inject(ActivatedRoute);
  _sharedService = inject(NavigationService);
  _storageDataTransferService = inject(StorageDataTransferService);
  _storageService = inject(StorageService);
  _env = inject(EnvironmentService);
  
  // Component properties
  currentUser: string;
  userRole: string;
  entities: any = [];
  selectedQuickAccess: any;
  currentEntity: string;
  quickAccessControls: QuickAccessControl = { deployments: null, licenseServerNames: null };
  syncHpcVersion: string = this._authService.SyncHPCVersion.replace("Version ", "V");
  expandMenu: boolean = false;
  subMenuItemsAvl: boolean = false;
  currentUrl: string = this._router.url;
  currentUrlParts = this.currentUrl.split("/");
  activeProperty: string;

  // Private properties
  private collapseTimer$ = new Subject<void>();
  private isHoveringMenuItem = false;
  private destroy$ = new Subject<void>();
  private autoCloseMenuInSeconds = this._env.envVariables.autoCloseMenuInSeconds || 15000;
  allDeployments: any[] = [];
  nonVdiDeployments: any[] = [];

  constructor(private cdRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.setupAutoCollapse();
    this.startCollapseTimer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSubscriptions(): void {
    // User authentication subscription
    this._authService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (user) => {
        if (user) {
          await this.handleUserLogin(user);
        } else {
          this.currentUser = null;
        }
      });

    // Router navigation subscription
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        this.currentUrlParts = this.currentUrl.split("/").filter(x => x !== "");
      });

    // Breadcrumb entity subscription - MOVED FROM ngAfterViewInit
    this._sharedService.breadcrumbEntity
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe((entity) => {
        // Use setTimeout to defer the update to the next tick
        setTimeout(() => {
          this.currentEntity = entity;
        }, 0);

        // Alternative approach using ChangeDetectorRef (uncomment if setTimeout doesn't work):
        // this.currentEntity = entity;
        // this.cdRef.detectChanges();
      });

    // Active property subscription
    this._sharedService.breadcrumbActiveProperty
      .pipe(
        filter(x => x !== ""),
        takeUntil(this.destroy$)
      )
      .subscribe((property) => {
        this.activeProperty = property?.split(" ")[0];
      });
  }

  private async handleUserLogin(user: any): Promise<void> {
    try {
      this.currentUser = user.username;
      const role = await lastValueFrom(this._userService.getUserRole(this.currentUser));

      this.userRole = this.mapUserRole(role);
      this.entities = await this._headerService.getEntities(this.userRole);
      this.quickAccessControls = await lastValueFrom(this._headerService.getQuickAccessControlSummary());

      this.filterQuickAccessForNonVdiEntities();
      this.filterEntitiesBasedOnVdiDeployments();
      this.configureEntityDropdowns();
      this.expandMenu = true;
    } catch (error) {
      console.error('Error handling user login:', error);
    }
  }

  private mapUserRole(role): string {
    const roleMap: { [key: number]: string } = {
      0: UserRoleNames.GlobalAdmin,
      1: UserRoleNames.DeploymentAdmin,
      3: UserRoleNames.JobOperator,
      4: UserRoleNames.Reader
    };
    return roleMap[role] || '';
  }

  private configureEntityDropdowns(): void {
    this.entities
      .filter(x => !["Users", "Sessions", "Analytics", "Licenses", "Configurator", "Home", "Repo", "AI Studio", "Images"].includes(x.name))
      .forEach(x => x.showMenuDropdown = true);
  }

  private setupAutoCollapse(): void {
    this.collapseTimer$
      .pipe(
        filter(() => !this.isHoveringMenuItem),
        debounceTime(this.autoCloseMenuInSeconds),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.expandMenu = false;
      });
  }

  startCollapseTimer(): void {
    this.isHoveringMenuItem = false;
    this.collapseTimer$.next();
  }

  cancelCollapseTimer(): void {
    this.isHoveringMenuItem = true;
  }

  openEntity(entity: any, event: Event): void {
    event.preventDefault();
    this._depService.dashboardReportRedirect = null;
    if (this.selectedQuickAccess) {
      this.redirectToDepEntity(entity);
    } else {
      this.handleDirectEntityNavigation(entity);
    }

    this._sharedService.subNavData(entity.name);
    this._storageDataTransferService.makeServiceClientAndShareClientNull();
    this.subMenuItemsAvl = false;
  }
  private handleDirectEntityNavigation(entity: any): void {
    if (entity.name === Entities.Reports) {
      this.selectedQuickAccess = this.quickAccessControls?.deployments[0];
      this.redirectToDepEntity(entity);
      return;
    }

    if (entity.name === Entities.Users && this.currentEntity === entity.name) {
      return;
    }

    if (entity.name === "AI Studio") {
      // **CHANGE THIS**: Directly navigate to Projects instead of AI Studio
      this._router.navigateByUrl("/AI Studio/Projects").then((success) => {
        if (success) {
          this.selectedQuickAccess = null;
          // Set the navigation service data to show submenus
          this._sharedService.subNavData(entity.name);
          // Highlight the Projects submenu
          this._sharedService.sendParentData("Projects");
        }
      }).catch((error) => {
        console.error('Navigation error:', error);
      });
      return;
    }

    this._router.navigate([entity.router], { relativeTo: this._route }).then(() => {
      this.selectedQuickAccess = null;
    });
  }

  openSubEntity(subMenu: any, entity: any, event?: Event): void {
    event?.preventDefault();
    // Handling for AI Studio submenus
    if (entity.name === 'AI Studio') {
      const aiStudioRoute = `/AI Studio/${subMenu.router}`;
      this._sharedService.sendParentData(subMenu.name);
      this._router.navigateByUrl(aiStudioRoute);
      return;
    }
    const reqUrl = this.buildSubEntityUrl(entity.name);
    const newUrl = this.checkIsLogUrl(reqUrl + "/" + subMenu.router, entity.name);

    this._sharedService.sendParentData(subMenu.name);
    this._router.navigateByUrl(newUrl);
  }

  private buildSubEntityUrl(entityName: string): string {
    const urlParts = this.currentUrlParts;

    switch (entityName) {
      case Entities.Storages:
        return urlParts.slice(0, 4).join("/");
      case Entities.Users:
      case Entities.Sessions:
        return urlParts.slice(0, 1).join("/");
      case Entities.LicenseServer:
        return urlParts.slice(0, 2).join("/");
      default:
        return urlParts.slice(0, 3).join("/");
    }
  }

  getEntitySubmenu(entity: any): any[] {
    const currentDeployment = this._sharedService.getCurrentDeployment();
    const dep = this._depService.getProfileInfoFromSubject(currentDeployment);

    if (entity.name === Entities.Storages) {
      return this.getStorageSubmenu(entity, dep);
    }

    if (entity.name === Entities.Nodes) {
      return this.getNodesSubmenu(entity, currentDeployment, dep);
    }

    return entity?.subMenu || [];
  }

  private getStorageSubmenu(entity: any, dep: any): any[] {
    if (this.userRole === UserRoleNames.JobOperator && dep?.showStorageProperties === false) {
      return entity?.subMenu.filter(x => x.name !== "Properties") || [];
    }
    return entity?.subMenu || [];
  }

  private getNodesSubmenu(entity: any, currentDeployment: any, dep: any): any[] {
    if (!currentDeployment) return entity?.subMenu || [];

    if (dep?.scheduler.toLowerCase() !== "kube") {
      return entity?.subMenu.filter(x => x.name !== "Pods") || [];
    }

    return entity?.subMenu || [];
  }

  checkIsLogUrl(url: string, entityName: string): string {
    if (url.toLowerCase().includes("log")) {
      const decodedUrl = decodeURIComponent(url);
      const urlTree = this._router.createUrlTree([decodedUrl], {
        queryParams: { type: entityName }
      });
      return this._router.serializeUrl(urlTree);
    }
    return url;
  }

  redirectToDepEntity(entity: any): void {
    this._router.navigate([entity.router]).then(() => {
      const { route, queryParams } = this.buildEntityRoute(entity);
      this._router.navigate([route], { queryParams });
      this.selectedQuickAccess = null;

      if (entity?.subMenu?.length > 0) {
        this.subMenuItemsAvl = true;
      }
    });

    this._storageDataTransferService.makeServiceClientAndShareClientNull();
    this._sharedService.sendChildData(this.selectedQuickAccess.name);
    this._sharedService.sendCloud(this.selectedQuickAccess.cloud);
    this._storageService.sendActiveStorageName(this.selectedQuickAccess.storageAccountName);
  }

  private buildEntityRoute(entity: any): { route: string; queryParams: any } {
    const access = this.selectedQuickAccess;
    let route = '';
    let queryParams = {};

    switch (entity.name) {
      case Entities.Storages:
        const storages = access.storageAccountName.split(",");
        if (storages.length > 1) {
          route = `${entity.name} List`;
          queryParams = { deployment: access.name };
        } else {
          route = `${entity.name} List/${access.cloud}/${access.name}/${storages[0]}`;
        }
        break;

      case Entities.Users:
        route = `Deployments List/${access.cloud}/${access.name}/users`;
        break;

      case Entities.LicenseServer:
        route = `License Servers List/${access}/licenses`;
        break;

      case Entities.Nodes:
        route = access === "All Nodes"
          ? `Nodes List/User/All/Node`
          : `${entity.name} List/${access.cloud}/${access.name}`;
        break;

      case Entities.Reports:
        route = `${entity.name}/${access.cloud}/${access.name}/reports`;
        break;
    
      default:
        route = `${entity.name} List/${access.cloud}/${access.name}`;
    }

    return { route, queryParams };
  }

  showDropdown(entity: string): boolean {
    if (["Users", "Sessions", "Dashboard", "Licenses", "Configurator", "Home","AI Studio", "Images"].includes(entity)) {
      return false;
    }

    const deploymentsForEntity = this.getDeploymentsForEntity(entity);
    return deploymentsForEntity.length > 0;
  }

  mouseEnter(idx: number): void {
    if (!this.quickAccessControls) return;

    const entity = this.entities[idx];
    const deploymentsToCheck = this.getDeploymentsForEntity(entity?.name);

    if (deploymentsToCheck?.length <= 1) return;

    try {
      // Check if myDrop and its results exist before accessing
      if (this.myDrop && this.myDrop._results && this.myDrop._results.length > idx) {
        this.myDrop._results.forEach((drop: any) => {
          if (drop && typeof drop.close === 'function') {
            drop.close();
          }
        });

        if (this.myDrop._results[idx] && this.myDrop._results[idx]._menu && typeof this.myDrop._results[idx].open === 'function') {
          this.myDrop._results[idx].open();
        }
      }
    } catch (error) {
      console.error('Error handling mouse enter:', error);
    }
  }

  showSubMenu(entity: any): boolean {
    return this.currentEntity === entity?.name;
  }

  mouseLeave(idx: number): void {
    try {
      // Check if myDrop and its results exist before accessing
      if (this.myDrop && this.myDrop._results && this.myDrop._results.length > idx) {
        if (this.myDrop._results[idx] && typeof this.myDrop._results[idx].close === 'function') {
          this.myDrop._results[idx].close();
        }
      }
    } catch (error) {
      console.error('Error handling mouse leave:', error);
    }
  }

  toggleMenu(val?: boolean): void {
    this.expandMenu = val !== undefined ? val : !this.expandMenu;

    if (this.expandMenu) {
      this.cancelCollapseTimer();
    } else {
      this.startCollapseTimer();
    }
  }

  private filterQuickAccessForNonVdiEntities(): void {
    if (this.quickAccessControls?.deployments) {
      this.allDeployments = [...this.quickAccessControls.deployments];
      this.nonVdiDeployments = this.quickAccessControls.deployments.filter(
        deployment => !deployment.isVdi
      );
    }
  }

  getDeploymentsForEntity(entityName: string): any[] {
    const entitiesWithNonVdiFilter = ['Storages', 'Jobs'];
    return entitiesWithNonVdiFilter.includes(entityName)
      ? this.nonVdiDeployments
      : this.allDeployments;
  }

  private filterEntitiesBasedOnVdiDeployments(): void {
    if (this.quickAccessControls?.deployments) {
      const hasNonVdiDeployments = this.nonVdiDeployments.length > 0;
      if (!hasNonVdiDeployments) {
        this.entities = this.entities.filter(entity =>
          !['Storages', 'Jobs'].includes(entity.name)
        );
      }
    }
  }

  trackBySubMenu(index: number, item: any): any {
    return item.name || index;
  }
}
